import React, { useState } from "react";
import api, { formatPrice } from "@/lib/api";
import { MapPin, Star, ThumbsUp, ThumbsDown, CheckCircle2 } from "lucide-react";

const FALLBACK = "https://images.unsplash.com/photo-1545597390-c92ae038a5a8?auto=format&fit=crop&w=1200&q=80";

export default function PortalProposals({ token, proposals, onChanged }) {
    if (proposals.length === 0) return <p className="text-xs text-ink-500">L'agenzia non ti ha ancora proposto immobili.</p>;
    return (
        <div className="grid sm:grid-cols-2 gap-4">
            {proposals.map((p) => <ProposalCard key={p.id} token={token} p={p} onChanged={onChanged} />)}
        </div>
    );
}

function ProposalCard({ token, p, onChanged }) {
    const [open, setOpen] = useState(false);
    const fb = p.feedback;
    return (
        <div data-testid={`portal-proposal-${p.id}`} className="border border-white/10 bg-ink-900/40 overflow-hidden">
            <img src={p.property_photo || FALLBACK} alt="" className="w-full h-40 object-cover" />
            <div className="p-4">
                <div className="font-serif text-ink-50">{p.property_title}</div>
                <div className="flex items-center gap-3 mt-1.5 text-[11px] text-ink-400">
                    <span className="flex items-center gap-1"><MapPin size={11} />{p.property_city}</span>
                    <span className="text-price">{formatPrice(p.property_price)}</span>
                </div>
                {fb ? (
                    <div data-testid={`portal-feedback-done-${p.id}`} className="mt-3 border border-green-700/30 bg-green-900/10 px-3 py-2 text-[11px] text-green-200 flex items-start gap-2">
                        <CheckCircle2 size={13} className="mt-0.5 shrink-0" />
                        <div>
                            Feedback inviato: {fb.interested ? "ti interessa" : "non ti interessa"}
                            {fb.rating ? ` · ${fb.rating}/5` : ""}{fb.comment ? ` — «${fb.comment}»` : ""}
                        </div>
                    </div>
                ) : open ? (
                    <FeedbackForm token={token} proposalId={p.id} onDone={() => { setOpen(false); onChanged(); }} />
                ) : (
                    <button data-testid={`portal-feedback-open-${p.id}`} onClick={() => setOpen(true)}
                        className="mt-3 w-full cta-outline py-2 text-xs">Lascia un feedback</button>
                )}
            </div>
        </div>
    );
}

function FeedbackForm({ token, proposalId, onDone }) {
    const [interested, setInterested] = useState(null);
    const [rating, setRating] = useState(0);
    const [comment, setComment] = useState("");
    const [busy, setBusy] = useState(false);

    const send = async () => {
        setBusy(true);
        try {
            await api.post(`/portal/${token}/proposals/${proposalId}/feedback`, { interested, rating: rating || null, comment });
            onDone();
        } finally { setBusy(false); }
    };

    return (
        <div className="mt-3 space-y-3">
            <div className="flex gap-2">
                <button data-testid={`fb-yes-${proposalId}`} onClick={() => setInterested(true)}
                    className={`flex-1 py-2 text-xs border flex items-center justify-center gap-1.5 transition-colors ${interested === true ? "border-green-500 text-green-300 bg-green-900/20" : "border-white/15 text-ink-400 hover:text-ink-100"}`}>
                    <ThumbsUp size={13} /> Mi interessa
                </button>
                <button data-testid={`fb-no-${proposalId}`} onClick={() => setInterested(false)}
                    className={`flex-1 py-2 text-xs border flex items-center justify-center gap-1.5 transition-colors ${interested === false ? "border-red-500 text-red-300 bg-red-900/20" : "border-white/15 text-ink-400 hover:text-ink-100"}`}>
                    <ThumbsDown size={13} /> No, grazie
                </button>
            </div>
            <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} data-testid={`fb-star-${n}-${proposalId}`} onClick={() => setRating(n)}>
                        <Star size={18} className={n <= rating ? "text-gold fill-gold" : "text-ink-600"} />
                    </button>
                ))}
                <span className="text-[10px] text-ink-500 ml-2">{rating ? `${rating}/5` : "Valutazione (opzionale)"}</span>
            </div>
            <textarea data-testid={`fb-comment-${proposalId}`} value={comment} onChange={(e) => setComment(e.target.value)} rows={2}
                placeholder="Un commento per l'agenzia (opzionale)..."
                className="w-full bg-transparent border border-white/15 px-3 py-2 text-xs text-ink-50 focus:border-gold outline-none" />
            <button data-testid={`fb-send-${proposalId}`} onClick={send} disabled={busy || interested === null}
                className="cta-gold w-full py-2.5 text-xs disabled:opacity-40">{busy ? "Invio..." : "Invia feedback"}</button>
        </div>
    );
}
